import {isEqual, omit, omitBy} from 'lodash';
import {Mutable} from 'utility-types';

import {isDraftMessage} from '../../../helpers/models/messageHelpers';
import {shouldUpdateResourceForUpdatedAt} from '../../../helpers/models/resourceHelpers';
import {MessageErrorsEnum} from '../../../models/api/messageErrorModel';
import {
  MessageModel,
  MessageStatusesEnum,
  MessageVisualStatusesEnum,
  PartialMessageModel,
} from '../../../models/api/messageModel';
import {AppAction} from '../../appActions';
import {ResourceMap} from '../../appTypes';
import {ConversationsActionsEnum} from '../conversations/conversationsActions';
import {DataGroupsActionsEnum, DataUpdatedAction} from '../dataGroupsActions';
import {MessageActionsEnum} from './messagesActions';

/*
 * Reducer.
 */

export function messagesReducer(
  state: ResourceMap<MessageModel>,
  action: AppAction,
): ResourceMap<MessageModel> {
  switch (action.type) {
    // Data Group.
    case DataGroupsActionsEnum.DATA_UPDATED:
      return messagesUpdatedReducer(state, action);

    // Single message deletion.
    case MessageActionsEnum.MESSAGE_DELETED:
      return omit(state, action.payload.messageUid);

    // Single conversation deletion / clearing conversation content.
    case ConversationsActionsEnum.CONVERSATION_ACCESS_DENIED:
    case ConversationsActionsEnum.CONVERSATION_DELETED:
    case ConversationsActionsEnum.CLEAR_CONVERSATION_CONTENT: {
      const {conversationId} = action.payload;

      // Remove the messages.
      return omitBy(state, (message) => message && message.conversationId === conversationId);
    }

    default:
      return state;
  }
}

function messagesUpdatedReducer(
  state: ResourceMap<MessageModel>,
  action: DataUpdatedAction,
): ResourceMap<MessageModel> {
  const {messages} = action.payload.result;
  if (!messages || messages.length === 0) {
    return state;
  }

  const {shouldIgnoreUpdatedAt} = action.payload;
  // Either the new state, or undefined if there are no state changes.
  let newState: Mutable<ResourceMap<MessageModel>> | undefined;

  // Optimization: iterate the updates once instead of _().groupBy/keyBy/sortBy/maxBy.
  messages.forEach((updatedMessage) => {
    const existingMessage = (newState || state)[updatedMessage.uid];
    if (!shouldUpdateMessage(existingMessage, updatedMessage, shouldIgnoreUpdatedAt)) {
      return;
    }

    // Optimization: create a copy of the state once on the first state change.
    newState = newState || {...state};
    newState[updatedMessage.uid] = updatedMessage;
  });

  return newState || state;
}

function shouldUpdateMessage(
  existingMessage: MessageModel | undefined,
  newMessage: MessageModel,
  shouldIgnoreUpdatedAt: boolean,
): boolean {
  // If there's no existing message, always use the new one.
  if (!existingMessage) {
    return true;
  }

  // If the message hasn't changed, don't update to preserve identity.
  if (isEqual(existingMessage, newMessage)) {
    return false;
  }

  // If the existing message is partial, always use the new one.
  // TODO: What's the recipients.length condition about?
  if (
    existingMessage.visualStatus === MessageVisualStatusesEnum.PARTIAL &&
    newMessage.recipients.length > 0
  ) {
    return true;
  }

  // We may not be allowed to overwrite this message.
  if (newMessage.visualStatus === MessageVisualStatusesEnum.PARTIAL) {
    return shouldUpdateWithPartialMessage(existingMessage, newMessage);
  }

  if (!canOverwriteDraft(existingMessage, newMessage)) {
    return false;
  }

  // Otherwise, replace if it is as least as recently updated.
  return (
    existingMessage.visualStatus === MessageVisualStatusesEnum.PARTIAL ||
    shouldUpdateResourceForUpdatedAt(existingMessage, newMessage, shouldIgnoreUpdatedAt)
  );
}

// Do not overwrite drafts that we were not able to save.
// Overwrite = existing copy has a save error that wasn't resolved by bumping to a new version.
function canOverwriteDraft(existingMessage: MessageModel, newMessage: MessageModel) {
  if (
    existingMessage.visualStatus !== MessageVisualStatusesEnum.DRAFT ||
    existingMessage.status === MessageStatusesEnum.DRAFT_SENDING ||
    existingMessage.status === MessageStatusesEnum.DRAFT_LATER ||
    newMessage.status === MessageStatusesEnum.DRAFT_SENDING ||
    newMessage.status === MessageStatusesEnum.DRAFT_LATER
  ) {
    return true;
  }

  if (newMessage.visualStatus === MessageVisualStatusesEnum.TIMELINE) {
    return true;
  }

  // We have a new version of the draft from the server that will replace current draft that has conflict.
  if (
    newMessage.visualStatus === MessageVisualStatusesEnum.DRAFT &&
    existingMessage.error?.type === MessageErrorsEnum.VERSION_CONFLICT
  ) {
    return true;
  }

  if (newMessage.visualStatus === MessageVisualStatusesEnum.DRAFT) {
    return newMessage.isSaving || existingMessage.version !== newMessage.version;
  }

  return !existingMessage.isSaving;
}

function shouldUpdateWithPartialMessage(existingMessage: MessageModel, newMessage: PartialMessageModel) {
  // Prevent a message to be locally stuck as sending
  // This can happen if real time events have been missed for instance
  if (isDraftMessage(existingMessage) && newMessage.status !== existingMessage.status) {
    return true;
  }

  return false;
}
